import type { Item, Decision } from '@mas-flow/types';
import { ApiHttpError } from './client';

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init });
  if (!res.ok) {
    const payload = await res.json().catch(() => undefined);
    throw new ApiHttpError(res.status, payload as { error: string; detail?: string } | undefined);
  }
  return (await res.json()) as T;
}

export async function list(includeArchived = false): Promise<Item[]> {
  const path = includeArchived ? '/api/items?includeArchived=1' : '/api/items';
  const { items } = await jsonFetch<{ items: Item[] }>(path);
  return items;
}

export async function getById(id: string): Promise<Item> {
  const { item } = await jsonFetch<{ item: Item }>(`/api/items/${encodeURIComponent(id)}`);
  return item;
}

export async function create(body: Partial<Item>): Promise<Item> {
  const { item } = await jsonFetch<{ item: Item }>('/api/items', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return item;
}

export async function patch(id: string, body: Partial<Item>, ifMatch: string): Promise<Item> {
  const { item } = await jsonFetch<{ item: Item }>(`/api/items/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'If-Match': ifMatch },
    body: JSON.stringify(body),
  });
  return item;
}

export interface RankInput {
  itemId: string;
  ifMatch: string;
  targetColumn: Decision | null;
  beforeRank: string | null;
  afterRank: string | null;
}

export async function rank(input: RankInput): Promise<Item> {
  const { itemId, ifMatch, ...body } = input;
  const { item } = await jsonFetch<{ item: Item }>(`/api/items/${encodeURIComponent(itemId)}/rank`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'If-Match': ifMatch },
    body: JSON.stringify(body),
  });
  return item;
}

export async function setAnchor(id: string, isAnchor: boolean, anchorWhy: string | null, ifMatch: string): Promise<Item> {
  const { item } = await jsonFetch<{ item: Item }>(`/api/items/${encodeURIComponent(id)}/anchor`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'If-Match': ifMatch },
    body: JSON.stringify({ isAnchor, anchorWhy }),
  });
  return item;
}

export async function setArchived(id: string, archive: boolean, ifMatch: string): Promise<Item> {
  const { item } = await jsonFetch<{ item: Item }>(`/api/items/${encodeURIComponent(id)}/archive`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'If-Match': ifMatch },
    body: JSON.stringify({ archive }),
  });
  return item;
}
